import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
  Table,
  Tabs
} from 'antd'
import Popconfirm from '../components/Popconfirm'
import { ArrowLeftOutlined, CheckCircleOutlined, CloseOutlined, SaveOutlined, UploadOutlined, DeleteOutlined, DownloadOutlined, DownOutlined, EditOutlined, EyeOutlined, FilePdfOutlined, MailOutlined, UpOutlined, UserAddOutlined, UserSwitchOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import * as api from '../lib/api'
import { DATE_FORMAT_LABEL } from '../lib/dateFormat'
import { delegationStatusPersonName } from './delegationConfig'
import RatingDistributionChart from './RatingDistributionChart'
import AppraisalWorkflowCard from './AppraisalWorkflowCard'
import SessionHRUserManager from './SessionHRUserManager'
import { delegationOptionsFromFlow, finalSessionDeadlineDate, flowFromDelegationOptions, personLabel, StatusBadge } from './HRDashboard'
import { useI18n } from '../i18n/I18nProvider'
import EmailTemplateEditor from './EmailTemplateEditor'
import ReadOnlySessionReport from './ReadOnlySessionReport'
import './SessionDetail.css'
import { completedRatingColumns } from './CompletedAppraisalRatings'

const { Title, Text } = Typography
const { TextArea } = Input
const AUTO_APPROVAL_STATUSES = ['pending_manager', 'pending_head', 'pending_bu_head', 'pending_approver_4', 'pending_approver_5']

const STAFF_ROLES = {
  employee: 'employee',
  manager: 'manager',
  department_head: 'department_head',
  bu_head: 'bu_head',
  approver_4: 'approver',
  approver_5: 'approver'
}

const EMAIL_TEMPLATE_TITLES = {
  department_head_notification: ['session.emails.types.approver_2_signoff', 'Approver 2 sign-off notification'],
  bu_head_notification: ['session.emails.types.approver_3_signoff', 'Approver 3 sign-off notification'],
  reminder_department_head: ['session.emails.types.reminder_approver_2', 'Reminder - Approver 2 sign-off'],
  reminder_bu_head: ['session.emails.types.reminder_approver_3', 'Reminder - Approver 3 sign-off'],
  reminder_employee: ['session.emails.types.reminder_employee', 'Employee reminder'],
  reminder_return_to_employee: ['session.emails.types.reminder_return_employee', 'Returned-to-employee reminder'],
  reminder_manager: ['session.emails.types.reminder_approver_1', 'Approver 1 reminder'],
  reminder_return_to_approver_1: ['session.emails.types.reminder_return_approver_1', 'Returned-to-Approver 1 reminder'],
  reminder_return_to_approver_2: ['session.emails.types.reminder_return_approver_2', 'Returned-to-Approver 2 reminder']
}

function emailTemplateTitle(template, t) {
  return EMAIL_TEMPLATE_TITLES[template.email_type] ? t(...EMAIL_TEMPLATE_TITLES[template.email_type]) : template.name
}

const EMAIL_TEMPLATE_GROUPS = [
  {
    titleKey: 'session.emails.groups.workflow.title',
    titleFallback: 'Workflow Notifications',
    descriptionKey: 'session.emails.groups.workflow.description',
    descriptionFallback: 'Individual employee notifications sent throughout the appraisal workflow.',
    types: ['appraisal_invite', 'employee_review_notification', 'return_to_employee', 'employee_completion_notification']
  }
]

function delegationStatusMeta(status) {
  const value = String(status || '').toLowerCase()
  if (value === 'revoked') return { label: 'Rejected', color: 'red' }
  if (value === 'pending') return { label: 'Pending', color: 'orange' }
  return { label: 'Completed', color: 'green' }
}

function staffLabel(user) {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
  const id = user?.company_employee_id ? ` (${user.company_employee_id})` : ''
  const position = user?.position ? ` - ${user.position}` : ''
  return `${name || user?.email}${id}${position} - ${user?.email}`
}

function staffOptions(users) {
  return (users || []).map(user => ({
    value: user.email,
    label: staffLabel(user),
    user
  }))
}

function delegationCandidateLabel(candidate) {
  const name = [candidate.first_name, candidate.last_name].filter(Boolean).join(' ').trim() || candidate.name
  return name && candidate.email ? `${name} (${candidate.email})` : (name || candidate.email)
}

function requestedReviews(appraisal) {
  return appraisal?.requested_functional_reviews || []
}

function DelegationTree({ appraisal, isHRSuperadmin, revokeFunctionalReview, t }) {
  const reviews = requestedReviews(appraisal)

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: reviews.length ? 6 : 0 }}>
        <span>{personLabel(appraisal.approver_1_first_name ?? appraisal.manager_first_name, appraisal.approver_1_last_name ?? appraisal.manager_last_name, appraisal.approver_1_email ?? appraisal.manager_email, appraisal.approver_1_position ?? appraisal.manager_position, appraisal.session_template)}</span>
      </div>
      {reviews.length > 0 && (
        <div style={{ marginLeft: 6, borderLeft: '1px solid #d9d9d9', paddingLeft: 12 }}>
          {reviews.map(review => (
            <Space key={review.id} size={4} wrap style={{ marginBottom: 4 }}>
              <Text type='secondary'>↳</Text>
              <Tag color='purple'>
                Requested Feedback from: {delegationStatusPersonName(review.reviewer_name)}
              </Tag>
              {(() => {
                const { label, color } = delegationStatusMeta(review.status)
                return <Tag color={color} style={{ margin: 0 }}>{label}</Tag>
              })()}
              {isHRSuperadmin && review.status === 'pending' && (
                <Popconfirm
                  title={t('delegation.confirm.revoke_indirect', 'Revoke this indirect delegation?')}
                  description={t('delegation.confirm.revoke_indirect_description', 'The reviewer will no longer be able to submit feedback.')}
                  okText={t('delegation.actions.revoke', 'Revoke')}
                  okButtonProps={{ danger: true }}
                  cancelText={t('common.cancel', 'Cancel')}
                  onConfirm={() => revokeFunctionalReview(appraisal.id, review.id)}
                >
                  <Button className='tti-btn-danger' size='small'>{t('delegation.actions.revoke', 'Revoke')}</Button>
                </Popconfirm>
              )}
            </Space>
          ))}
        </div>
      )}
    </div>
  )
}

function daysUntil(dateStr) {
  if (!dateStr) return null
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24))
}

export default function SessionDetail() {
  const { t } = useI18n()
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [justSaved, setJustSaved] = useState(false)

  const [me, setMe] = useState(null)
  const [session, setSession] = useState(null)
  const [appraisals, setAppraisals] = useState([])
  const [appraisalSearch, setAppraisalSearch] = useState('')
  const [debouncedAppraisalSearch, setDebouncedAppraisalSearch] = useState('')
  const [appraisalDepartment, setAppraisalDepartment] = useState()
  const [appraisalStatus, setAppraisalStatus] = useState()
  const [appraisalPage, setAppraisalPage] = useState(1)
  const [bulkActionType, setBulkActionType] = useState(null)
  const [selectedBulkIds, setSelectedBulkIds] = useState([])
  const [downloadingBulkPdf, setDownloadingBulkPdf] = useState(false)
  const [autoApproving, setAutoApproving] = useState(false)
  const [appraisalTotal, setAppraisalTotal] = useState(0)
  const [appraisalSummary, setAppraisalSummary] = useState({
    total: 0,
    status_counts: {},
    departments: []
  })

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([])

  const [employeeModalOpen, setEmployeeModalOpen] = useState(false)
  const [addEmployeeForm] = Form.useForm()
  const [reviewPeriods, setReviewPeriods] = useState([])
  const [periodSaving, setPeriodSaving] = useState({})
  const [emailTemplates, setEmailTemplates] = useState([])
  const [emailTemplateSaving, setEmailTemplateSaving] = useState({})
  const [templates, setTemplates] = useState([])
  const [businessUnits, setBusinessUnits] = useState([])
  const [departments, setDepartments] = useState([])
  const [tabLoading, setTabLoading] = useState({})
  const [tabRefreshKey, setTabRefreshKey] = useState(0)
  const [appraisalPageLoading, setAppraisalPageLoading] = useState(false)
  const [editingAppraisal, setEditingAppraisal] = useState(null)
  const [staffSaving, setStaffSaving] = useState(false)
  const [staffSearchResults, setStaffSearchResults] = useState({
    employee: [],
    manager: [],
    department_head: [],
    bu_head: [],
    approver_4: [],
    approver_5: []
  })

  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importProgress, setImportProgress] = useState(0)
  const [importUploadProgress, setImportUploadProgress] = useState(0)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const [importJobId, setImportJobId] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [downloadingImportExample, setDownloadingImportExample] = useState(false)
  const [testEmailModalOpen, setTestEmailModalOpen] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [sendingTestEmail, setSendingTestEmail] = useState(false)
  const [sendingAllTestEmails, setSendingAllTestEmails] = useState(false)
  const [sendingTemplateTestEmail, setSendingTemplateTestEmail] = useState({})
  const [sendingEmployeeInvitation, setSendingEmployeeInvitation] = useState({})
  const [delegationAppraisal, setDelegationAppraisal] = useState(null)
  const [hrDelegationMode, setHrDelegationMode] = useState('functional_review')
  const [hrDelegateCandidates, setHrDelegateCandidates] = useState([])
  const [hrDelegateTo, setHrDelegateTo] = useState([])
  const [hrAddCandidateOpen, setHrAddCandidateOpen] = useState(false)
  const [hrExistingCandidateToAdd, setHrExistingCandidateToAdd] = useState(null)
  const [hrCreatingCandidate, setHrCreatingCandidate] = useState(false)
  const [hrNewCandidate, setHrNewCandidate] = useState({ email: '', first_name: '', last_name: '', position: '' })
  const [hrDelegationLoading, setHrDelegationLoading] = useState(false)
  const [hrDelegationError, setHrDelegationError] = useState('')
  const [hrDelegating, setHrDelegating] = useState(false)
  const [form] = Form.useForm()
  const selectedTemplateCode = Form.useWatch('template', form)
  const selectedEmployeeBusinessUnit = Form.useWatch('business_unit_id', addEmployeeForm)
  const selectedEmployeeBusinessUnitName = Form.useWatch('business_unit_name', addEmployeeForm)
  const pageTopRef = useRef(null)
  const appraisalsRef = useRef(null)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedAppraisalSearch(appraisalSearch)
      setAppraisalPage(1)
    }, 350)
    return () => window.clearTimeout(timer)
  }, [appraisalSearch])

  const loadAppraisalPage = useCallback(async (page) => {
    if (!id) return
    setAppraisalPageLoading(true)
    try {
      const response = await api.getAppraisals({
        session: id,
        page,
        page_size: 30,
        q: debouncedAppraisalSearch || undefined,
        department: appraisalDepartment || undefined,
        status: appraisalStatus || undefined
      })
      setAppraisals(response.results || [])
      setAppraisalTotal(response.count || 0)
      if (response.summary) setAppraisalSummary(response.summary)
      setAppraisalPage(page)
    } catch (e) {
      message.error(e.message || t('appraisals.session.messages.refresh_failed', 'Unable to refresh appraisals'))
    } finally {
      setAppraisalPageLoading(false)
    }
  }, [appraisalDepartment, appraisalStatus, debouncedAppraisalSearch, id, t])

  useEffect(() => {
    loadAppraisalPage(appraisalPage)
  }, [appraisalPage, appraisalDepartment, appraisalStatus, debouncedAppraisalSearch, loadAppraisalPage])

  async function changeTab(key) {
    const next = new URLSearchParams(searchParams)
    next.set('tab', key)
    setSearchParams(next, { replace: true })
    setTabRefreshKey(current => current + 1)

    if (key !== 'appraisals' || session?.read_only_report_type) return

    setTabLoading(previous => ({ ...previous, appraisals: true }))
    try {
      await loadAppraisalPage(appraisalPage)
    } catch (e) {
      message.error(e.message || t('appraisals.session.messages.refresh_failed', 'Unable to refresh appraisals'))
    } finally {
      setTabLoading(previous => ({ ...previous, appraisals: false }))
    }
  }

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError(null)
      setReviewPeriods([])
      setEmailTemplates([])
      setTemplates([])
      setBusinessUnits([])
      try {
        const [meData, sessionData] = await Promise.all([
          api.getMe(),
          api.getSession(id)
        ])
        setMe(meData)
        setSession(sessionData)

        setSelectedEmployeeIds(sessionData.employees || [])

        form.setFieldsValue({
          name: sessionData.name,
          display_title: sessionData.display_title,
          template: sessionData.template,
          description: sessionData.description,
          performance_period_start_date: sessionData.performance_period_start_date ? dayjs(sessionData.performance_period_start_date) : null,
          performance_period_end_date: sessionData.performance_period_end_date ? dayjs(sessionData.performance_period_end_date) : null,
          employee_stage_start_date: sessionData.employee_stage_start_date ? dayjs(sessionData.employee_stage_start_date) : null,
          employee_stage_end_date: sessionData.employee_stage_end_date ? dayjs(sessionData.employee_stage_end_date) : null,
          manager_stage_start_date: sessionData.manager_stage_start_date ? dayjs(sessionData.manager_stage_start_date) : null,
          manager_stage_end_date: sessionData.manager_stage_end_date ? dayjs(sessionData.manager_stage_end_date) : null,
          department_head_stage_start_date: sessionData.department_head_stage_start_date ? dayjs(sessionData.department_head_stage_start_date) : null,
          department_head_stage_end_date: sessionData.department_head_stage_end_date ? dayjs(sessionData.department_head_stage_end_date) : null,
          bu_head_stage_start_date: sessionData.bu_head_stage_start_date ? dayjs(sessionData.bu_head_stage_start_date) : null,
          bu_head_stage_end_date: sessionData.bu_head_stage_end_date ? dayjs(sessionData.bu_head_stage_end_date) : null,
          manager_delegation_options: delegationOptionsFromFlow(sessionData.manager_review_flow || 'direct_transfer'),
          rating_justification_min_length: sessionData.rating_justification_min_length || 10,
          status: sessionData.status
        })
      } catch (e) {
        setError(e.message || t('session.messages.load_failed', 'Unable to load session'))
      } finally {
        setLoading(false)
      }
    }

    if (id) load()
  }, [id, form, t])

  const userRoles = me?.roles || []
  const isGlobalHRSuperadmin = (
    userRoles.includes('global_hr_superadmin') ||
    userRoles.includes('global_hr_tti_milwaukee_asia')
  )
  const isHRSuperadmin = (
    userRoles.includes('hr_superadmin') ||
    userRoles.includes('frontend_superadmin') ||
    isGlobalHRSuperadmin
  )
  const isHRViewer = (
    userRoles.includes('hr_viewer') ||
    userRoles.includes('global_hr')
  )
  const isHR = isHRSuperadmin || isHRViewer
  const bulkActionForAppraisal = record => {
    if (record.status === 'completed' && record.can_export_pdf) return 'pdf'
    if (isHRSuperadmin && AUTO_APPROVAL_STATUSES.includes(record.status)) return 'auto-approve'
    return null
  }
  const employeeListHidden = Boolean(session?.employee_list_hidden)
  const isReadOnlySession = Boolean(session?.read_only_report_type)
  const hasLimitedSessionTabs = employeeListHidden && isGlobalHRSuperadmin
  const allowedSessionTabs = isReadOnlySession
    ? (isHRSuperadmin ? ['appraisals', 'imported-report', 'hr-users'] : ['appraisals'])
    : hasLimitedSessionTabs
      ? ['appraisals', 'hr-users']
      : isHRSuperadmin
        ? ['appraisals', 'review-periods', 'emails', 'properties', 'hr-users']
        : ['appraisals', 'review-periods', 'emails', 'properties']
  const activeTab = allowedSessionTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : allowedSessionTabs[0]
  const hrSupportsDirectDelegation = ['direct_transfer', 'both'].includes(session?.manager_review_flow)
  const hrSupportsReviewDelegation = ['functional_review', 'both'].includes(session?.manager_review_flow)
  const visibleReviewPeriods = reviewPeriods

  useEffect(() => {
    if (!session || `${session.id}` !== `${id}`) return

    const loadTab = async () => {
      setTabLoading(previous => ({ ...previous, [activeTab]: true }))
      try {
        if (activeTab === 'review-periods') {
          setReviewPeriods(await api.getSessionReviewPeriods(id))
        } else if (activeTab === 'emails') {
          setEmailTemplates(await api.getSessionEmailTemplates(id))
        } else if (activeTab === 'properties') {
          const templateOptions = await api.getTemplateOptions()
          setTemplates(templateOptions)
          const selectedTemplate = templateOptions.find(template => template.code === session.template)
          setBusinessUnits(await api.getBusinessUnitOptions({
            region: session.region,
            organization: selectedTemplate?.organization
          }))
        }
      } catch (e) {
        message.error(e.message || t('session.messages.tab_load_failed', 'Unable to load this section'))
      } finally {
        setTabLoading(previous => ({ ...previous, [activeTab]: false }))
      }
    }

    loadTab()
  }, [activeTab, id, isHRSuperadmin, session, t, tabRefreshKey])

  // Selected employees list is intentionally not rendered in the modal anymore.

  useEffect(() => {
    if (employeeModalOpen) {
      async function loadOrganizationOptions() {
        try {
          const templateOptions = await api.getTemplateOptions()
          setTemplates(templateOptions)
          const selectedTemplate = templateOptions.find(template => template.code === session?.template)
          const [units, referenceDepartments, sessionDepartments] = await Promise.all([
            api.getBusinessUnitOptions({
              region: session?.region,
              organization: selectedTemplate?.organization
            }),
            api.getDepartments({
              region: session?.region,
              organization: selectedTemplate?.organization
            }),
            api.getSessionDepartments(id)
          ])
          setBusinessUnits(units)
          setDepartments([
            ...sessionDepartments.map(department => ({ ...department, isSessionDepartment: true })),
            ...referenceDepartments.filter(department =>
              !sessionDepartments.some(existing =>
                existing.name.toLowerCase() === department.name.toLowerCase() &&
                existing.business_unit === department.business_unit
              )
            )
          ])
        } catch (e) {
          message.error(e.message || t('organization.messages.options_load_failed', 'Unable to load business units and departments'))
        }
      }

      loadOrganizationOptions()
      api.searchStaff({ role: STAFF_ROLES.employee, sessionId: id }).then(users => {
        setStaffSearchResults(prev => ({ ...prev, employee: users }))
      }).catch(() => undefined)
      api.searchStaff({ role: STAFF_ROLES.manager, sessionId: id }).then(users => {
        setStaffSearchResults(prev => ({ ...prev, manager: users }))
      }).catch(() => undefined)
      api.searchStaff({ role: STAFF_ROLES.department_head, sessionId: id }).then(users => {
        setStaffSearchResults(prev => ({ ...prev, department_head: users }))
      }).catch(() => undefined)
      api.searchStaff({ role: STAFF_ROLES.bu_head, sessionId: id }).then(users => {
        setStaffSearchResults(prev => ({ ...prev, bu_head: users }))
      }).catch(() => undefined)
      api.searchStaff({ role: STAFF_ROLES.approver_4, sessionId: id }).then(users => {
        setStaffSearchResults(prev => ({ ...prev, approver_4: users, approver_5: users }))
      }).catch(() => undefined)
    }
  }, [employeeModalOpen, id, session, t])

  const fillPersonFields = (prefix, user) => {
    if (!user) return
    addEmployeeForm.setFieldsValue({
      [`${prefix}_email`]: user.email || '',
      [`${prefix}_first_name`]: user.first_name || '',
      [`${prefix}_last_name`]: user.last_name || '',
      ...(prefix === 'employee' ? {
        employee_company_id: user.company_employee_id || '',
        employee_position: user.position || ''
      } : {})
    })
  }

  const searchStaff = async (kind, value) => {
    const q = (value || '').trim()
    if (q && q.length < 2) return
    try {
      const users = await api.searchStaff({ q, role: STAFF_ROLES[kind], sessionId: id })
      setStaffSearchResults(prev => ({ ...prev, [kind]: users }))
    } catch (_) {
      setStaffSearchResults(prev => ({ ...prev, [kind]: [] }))
    }
  }

  const selectStaff = (kind, user) => {
    fillPersonFields(kind, user)
    if (kind !== 'employee') return

    if (user.manager) fillPersonFields('manager', user.manager)
    if (user.department_head) fillPersonFields('department_head', user.department_head)
    if (user.bu_head) fillPersonFields('bu_head', user.bu_head)
    if (user.business_unit) {
      addEmployeeForm.setFieldsValue({
        business_unit_id: user.business_unit.id,
        business_unit_name: user.business_unit.name
      })
    }
    if (user.department) {
      addEmployeeForm.setFieldValue('department_name', user.department.name)
    }
  }

  const selectDepartment = (_, option) => {
    const department = option.department
    if (!department) return
    if (department.business_unit) {
      const businessUnit = businessUnits.find(unit => unit.id === department.business_unit)
      addEmployeeForm.setFieldsValue({
        business_unit_id: department.business_unit,
        business_unit_name: businessUnit?.name
      })
    }
    if (department.head_email) {
      fillPersonFields('department_head', {
        email: department.head_email,
        first_name: department.head_first_name,
        last_name: department.head_last_name,
        company_employee_id: department.head_company_id,
        position: department.head_position
      })
    }
    if (department.bu_head) fillPersonFields('bu_head', department.bu_head)
  }

  const refreshSessionAppraisals = async () => {
    const [updatedSession] = await Promise.all([
      api.getSession(id),
      loadAppraisalPage(appraisalPage)
    ])
    setSession(updatedSession)
    setSelectedEmployeeIds(updatedSession.employees || [])
  }

  const loadHrDelegateCandidates = async (appraisal, mode) => {
    setHrDelegationLoading(true)
    setHrDelegationError('')
    try {
      const response = await api.getAppraisalDelegateCandidates(appraisal.id, mode)
      setHrDelegateCandidates(response.candidates || [])
    } catch (e) {
      setHrDelegateCandidates([])
      setHrDelegationError(e.message || t('delegation.messages.candidates_load_failed', 'Unable to load delegation candidates'))
    } finally {
      setHrDelegationLoading(false)
    }
  }

  const delegationSelectionForMode = (appraisal, mode) => {
    if (mode === 'functional_review') {
      return (appraisal.requested_functional_reviews || [])
        .filter(review => review.status === 'pending')
        .map(review => review.reviewer_id)
    }
    return null
  }

  const openHrDelegationModal = async (appraisal) => {
    const mode = hrSupportsReviewDelegation ? 'functional_review' : 'direct_transfer'
    setDelegationAppraisal(appraisal)
    setHrDelegationMode(mode)
    setHrDelegateTo(delegationSelectionForMode(appraisal, mode))
    setHrDelegateCandidates([])
    setHrDelegationError('')
    await loadHrDelegateCandidates(appraisal, mode)
  }

  const changeHrDelegationMode = async (mode) => {
    setHrDelegationMode(mode)
    setHrDelegateTo(delegationSelectionForMode(delegationAppraisal, mode))
    setHrDelegateCandidates([])
    setHrDelegationError('')
    if (delegationAppraisal) await loadHrDelegateCandidates(delegationAppraisal, mode)
  }

  const closeHrDelegationModal = () => {
    setDelegationAppraisal(null)
    setHrAddCandidateOpen(false)
    setHrDelegateCandidates([])
    setHrDelegateTo([])
    setHrExistingCandidateToAdd(null)
    setHrDelegationError('')
  }

  const openHrAddCandidate = () => {
    setHrExistingCandidateToAdd(null)
    setHrNewCandidate({ email: '', first_name: '', last_name: '', position: '' })
    setHrAddCandidateOpen(true)
  }

  const confirmHrAddCandidate = async () => {
    if (!delegationAppraisal) return
    if (hrExistingCandidateToAdd) {
      setHrDelegateTo(current => hrDelegationMode === 'functional_review'
        ? [...new Set([...(Array.isArray(current) ? current : []), hrExistingCandidateToAdd])]
        : hrExistingCandidateToAdd)
      setHrExistingCandidateToAdd(null)
      setHrAddCandidateOpen(false)
      message.success(t('delegation.messages.reviewer_selected', 'Reviewer selected'))
      return
    }
    setHrCreatingCandidate(true)
    try {
      const response = await api.createDelegateCandidate(
        [delegationAppraisal.id],
        hrNewCandidate,
        hrDelegationMode
      )
      const candidate = response.candidate
      setHrDelegateCandidates(current => [...current.filter(item => item.id !== candidate.id), candidate])
      setHrDelegateTo(current => hrDelegationMode === 'functional_review'
        ? [...new Set([...(Array.isArray(current) ? current : []), candidate.id])]
        : candidate.id)
      setHrNewCandidate({ email: '', first_name: '', last_name: '', position: '' })
      setHrAddCandidateOpen(false)
      const finalAction = hrDelegationMode === 'functional_review'
        ? t('delegation.actions.request_feedback', 'Request Feedback')
        : t('delegation.actions.transfer_approver', 'Transfer Approver')
      message.success(t(
        response.created ? 'delegation.messages.created_preselected' : 'delegation.messages.existing_preselected',
        response.created
          ? 'Reviewer created and preselected. Click "{{action}}" to complete the delegation.'
          : 'Existing reviewer selected and preselected. Click "{{action}}" to complete the delegation.',
        { action: finalAction }
      ))
    } catch (e) {
      setHrDelegationError(e.message || t('delegation.messages.manager_add_failed', 'Unable to add manager'))
      message.error(e.message || t('delegation.messages.manager_add_failed', 'Unable to add manager'))
    } finally {
      setHrCreatingCandidate(false)
    }
  }

  const submitHrDelegation = async () => {
    const selectedIds = Array.isArray(hrDelegateTo) ? hrDelegateTo : (hrDelegateTo ? [hrDelegateTo] : [])
    if (!delegationAppraisal || selectedIds.length === 0) return
    setHrDelegating(true)
    try {
      await api.delegateAppraisal(delegationAppraisal.id, selectedIds, hrDelegationMode)
      message.success(hrDelegationMode === 'functional_review'
        ? t('delegation.messages.feedback_requested', 'Reviewer feedback requested')
        : t('delegation.messages.approver_transferred', 'Approver transferred'))
      closeHrDelegationModal()
      await refreshSessionAppraisals()
    } catch (e) {
      setHrDelegationError(e.message || t('delegation.messages.failed', 'Delegation failed'))
    } finally {
      setHrDelegating(false)
    }
  }

  const revokeFunctionalReview = async (appraisalId, reviewId) => {
    try {
      await api.revokeFunctionalReview(appraisalId, reviewId)
      message.success(t('delegation.messages.indirect_revoked', 'Indirect delegation revoked'))
      await refreshSessionAppraisals()
    } catch (e) {
      message.error(e.message || t('delegation.messages.indirect_revoke_failed', 'Unable to revoke indirect delegation'))
    }
  }

  const closeEmployeeModal = () => {
    setEmployeeModalOpen(false)
    setEditingAppraisal(null)
    addEmployeeForm.resetFields()
  }

  const openAddStaffModal = () => {
    setEditingAppraisal(null)
    addEmployeeForm.resetFields()
    setEmployeeModalOpen(true)
  }

  const openEditStaffModal = async (appraisal) => {
    setEditingAppraisal(appraisal)
    setEmployeeModalOpen(true)
    try {
      const detail = await api.getAppraisal(appraisal.id)
      const staffValues = {
        employee_email: detail.employee_email,
        employee_first_name: detail.employee_first_name,
        employee_last_name: detail.employee_last_name,
        employee_company_id: detail.employee_company_id,
        employee_date_joined: detail.employee_date_joined ? dayjs(detail.employee_date_joined) : null,
        employee_position: detail.employee_position,
        employee_employment_status: detail.employee_employment_status || 'active',
        manager_email: detail.approver_1_email ?? detail.manager_email,
        manager_first_name: detail.approver_1_first_name ?? detail.manager_first_name,
        manager_last_name: detail.approver_1_last_name ?? detail.manager_last_name,
        department_name: detail.department_name || '',
        business_unit_id: detail.business_unit || null,
        business_unit_name: detail.business_unit_name || '',
        department_head_email: detail.approver_2_email ?? detail.department_head_email,
        department_head_first_name: detail.approver_2_first_name ?? detail.department_head_first_name,
        department_head_last_name: detail.approver_2_last_name ?? detail.department_head_last_name
      }
      Object.assign(staffValues, {
        bu_head_email: detail.approver_3_email ?? detail.bu_head_email,
        bu_head_first_name: detail.approver_3_first_name ?? detail.bu_head_first_name,
        bu_head_last_name: detail.approver_3_last_name ?? detail.bu_head_last_name
      })
      for (const level of [4, 5]) {
        staffValues[`approver_${level}_email`] = detail[`approver_${level}_email`]
        staffValues[`approver_${level}_first_name`] = detail[`approver_${level}_first_name`]
        staffValues[`approver_${level}_last_name`] = detail[`approver_${level}_last_name`]
      }
      addEmployeeForm.setFieldsValue(staffValues)
    } catch (e) {
      message.error(e.message || t('appraisals.session.messages.staff_load_failed', 'Failed to load appraisal staff details'))
    }
  }

  const saveStaff = async () => {
    setStaffSaving(true)
    try {
      const values = await addEmployeeForm.validateFields()
      const payload = {
        employee_email: values.employee_email,
        employee_first_name: values.employee_first_name,
        employee_last_name: values.employee_last_name,
        employee_company_id: values.employee_company_id,
        employee_date_joined: values.employee_date_joined?.format('YYYY-MM-DD') || null,
        employee_position: values.employee_position,
        employee_employment_status: values.employee_employment_status || 'active',
        approver_1_email: values.manager_email,
        approver_1_first_name: values.manager_first_name,
        approver_1_last_name: values.manager_last_name,
        department_name: values.department_name,
        business_unit_id: values.business_unit_id,
        business_unit_name: values.business_unit_name,
        approver_2_email: values.department_head_email,
        approver_2_first_name: values.department_head_first_name,
        approver_2_last_name: values.department_head_last_name,
        approver_3_email: values.bu_head_email,
        approver_3_first_name: values.bu_head_first_name,
        approver_3_last_name: values.bu_head_last_name
      }
      for (const level of [4, 5]) {
        payload[`approver_${level}_email`] = values[`approver_${level}_email`]
        payload[`approver_${level}_first_name`] = values[`approver_${level}_first_name`]
        payload[`approver_${level}_last_name`] = values[`approver_${level}_last_name`]
      }

      if (editingAppraisal) {
        await api.updateAppraisalStaff(editingAppraisal.id, payload)
      } else {
        await api.addSessionEmployee(id, payload)
      }

      addEmployeeForm.resetFields()
      setEmployeeModalOpen(false)
      setEditingAppraisal(null)
      message.success(editingAppraisal
        ? t('appraisals.session.messages.staff_updated', 'Appraisal staff updated')
        : t('appraisals.session.messages.employee_added', 'Employee added'))
      await refreshSessionAppraisals()
    } catch (e) {
      if (e?.message) message.error(e.message)
    } finally {
      setStaffSaving(false)
    }
  }

  // removeEmployee kept for the main list delete button; modal no longer uses it.

  const confirmSaveSession = (nextStatus, values) => {
    const isDeactivating = session?.status === 'active' && nextStatus === 'closed'

    Modal.confirm({
      title: isDeactivating ? t('dashboard.confirm.deactivate.title', 'Deactivate this session?') : t('session.confirm.save.title', 'Save session changes?'),
      content: isDeactivating
        ? t('dashboard.confirm.deactivate.description', 'Employees and managers will no longer be able to continue actions in this session until it is reactivated.')
        : t('session.confirm.save.description', 'Your session changes will be saved.'),
      okText: isDeactivating ? t('dashboard.actions.deactivate', 'Deactivate') : t('common.save_changes', 'Save Changes'),
      cancelText: t('common.cancel', 'Cancel'),
      okButtonProps: isDeactivating ? { danger: true } : undefined,
      onOk: () => saveAll(values)
    })
  }

  const saveAll = async (validatedValues) => {
    setSaving(true)
    setError(null)
    try {
      const values = validatedValues || await form.validateFields()

      const payload = {
        name: values.name,
        template: values.template,
        description: values.description,
        performance_period_start_date: values.performance_period_start_date?.format('YYYY-MM-DD') || null,
        performance_period_end_date: values.performance_period_end_date?.format('YYYY-MM-DD') || null,
        manager_review_flow: flowFromDelegationOptions(values.manager_delegation_options),
        status: values.status,
        employees: selectedEmployeeIds
      }
      if (values.template === 'vietnam_milwaukee') {
        payload.rating_justification_min_length = values.rating_justification_min_length || 10
      }

      await api.updateSession(id, payload)

      const updated = await api.getSession(id)
      setSession(updated)
      setSelectedEmployeeIds(updated.employees || [])

      message.success(t('session.messages.saved', 'Session saved'))
      setJustSaved(true)
      setTimeout(() => {
        setJustSaved(false)
      }, 900)
    } catch (e) {
      Modal.error({
        title: t('session.messages.save_failed', 'Unable to save session'),
        content: <div style={{ whiteSpace: 'pre-wrap' }}>{e.message}</div>
      })
    } finally {
      setSaving(false)
    }
  }

  function updateReviewPeriodLocal(periodId, updates) {
    setReviewPeriods(prev => prev.map(item => item.id === periodId ? { ...item, ...updates } : item))
  }

  async function saveReviewPeriod(period) {
    if (!period.start_date || !period.end_date) {
      message.error(t('session.review_periods.validation.dates_required', 'Start date and end date are required for every review period.'))
      return
    }
    setPeriodSaving(prev => ({ ...prev, [period.id]: true }))
    try {
      const updated = await api.updateSessionReviewPeriod(id, period.id, {
        start_date: period.start_date || null,
        end_date: period.end_date || null,
        deadline_type: period.deadline_type || 'soft'
      })
      setReviewPeriods(prev => prev.map(item => item.id === updated.id ? updated : item))
      const refreshed = await api.getSession(id)
      setSession(refreshed)
      message.success(t('session.review_periods.messages.saved', 'Review period saved'))
    } catch (e) {
      message.error(e.message || t('session.review_periods.messages.save_failed', 'Unable to save review period'))
    } finally {
      setPeriodSaving(prev => ({ ...prev, [period.id]: false }))
    }
  }

  async function sendTestEmails() {
    setSendingTestEmail(true)
    try {
      const res = await api.sendSessionTestEmails(id, testEmail)
      message.success(t('session.emails.messages.test_sent', 'Sent {{count}} test email(s) to {{email}}', { count: res.sent, email: res.email }))
      setTestEmailModalOpen(false)
      setTestEmail('')
    } catch (e) {
      message.error(e.message || t('session.emails.messages.tests_failed', 'Unable to send test emails'))
    } finally {
      setSendingTestEmail(false)
    }
  }

  function updateEmailTemplateLocal(templateId, updates) {
    setEmailTemplates(prev => prev.map(template => template.id === templateId ? { ...template, ...updates } : template))
  }

  async function saveEmailTemplate(template) {
    setEmailTemplateSaving(prev => ({ ...prev, [template.id]: true }))
    try {
      const updated = await api.updateSessionEmailTemplate(id, template.id, {
        subject_template: template.subject_template,
        body_template: template.body_template,
        is_active: template.is_active
      })
      setEmailTemplates(prev => prev.map(item => item.id === updated.id ? updated : item))
      message.success(t('session.emails.messages.template_saved', 'Email template saved'))
    } catch (e) {
      message.error(e.message || t('session.emails.messages.template_save_failed', 'Unable to save email template'))
    } finally {
      setEmailTemplateSaving(prev => ({ ...prev, [template.id]: false }))
    }
  }

  function confirmSendAllTestEmails() {
    if (!me?.email) {
      message.error(t('session.emails.messages.current_user_no_email', 'Current user does not have an email address.'))
      return
    }
    Modal.confirm({
      title: t('session.emails.confirm.test_all.title', 'Send all test emails?'),
      content: t('session.emails.confirm.test_all.description', "Are you sure you would like to send all configured test emails to the current user's email address ({{email}})?", { email: me.email }),
      okText: t('session.emails.actions.send_tests', 'Send Test Emails'),
      onOk: async () => {
        setSendingAllTestEmails(true)
        try {
          const res = await api.sendAllSessionTestEmailsToCurrentUser(id)
          message.success(t('session.emails.messages.test_sent', 'Sent {{count}} test email(s) to {{email}}', { count: res.sent, email: res.email }))
        } catch (e) {
          message.error(e.message || t('session.emails.messages.tests_failed', 'Unable to send test emails'))
        } finally {
          setSendingAllTestEmails(false)
        }
      }
    })
  }

  function confirmSendTemplateTestEmail(template) {
    if (!me?.email) {
      message.error(t('session.emails.messages.current_user_no_email', 'Current user does not have an email address.'))
      return
    }
    Modal.confirm({
      title: t('session.emails.confirm.test_one.title', 'Test {{template}}?', { template: emailTemplateTitle(template, t) }),
      content: t('session.emails.confirm.test_one.description', 'Are you sure you would like to send a test email for "{{template}}" to the current user\'s email address ({{email}})?', { template: emailTemplateTitle(template, t), email: me.email }),
      okText: t('session.emails.actions.send_test', 'Send Test Email'),
      onOk: async () => {
        setSendingTemplateTestEmail(prev => ({ ...prev, [template.id]: true }))
        try {
          const res = await api.sendSessionTemplateTestEmailToCurrentUser(id, template)
          message.success(t('session.emails.messages.test_sent', 'Sent {{count}} test email(s) to {{email}}', { count: res.sent, email: res.email }))
        } catch (e) {
          message.error(e.message || t('session.emails.messages.test_failed', 'Unable to send test email'))
        } finally {
          setSendingTemplateTestEmail(prev => ({ ...prev, [template.id]: false }))
        }
      }
    })
  }

  function confirmSendEmployeeInvitation(appraisal) {
    Modal.confirm({
      title: t('session.invitations.confirm.title', 'Send employee invitation?'),
      content: t('session.invitations.confirm.description', 'Send an appraisal invitation to {{email}}?', { email: appraisal.employee_email }),
      okText: t('session.invitations.actions.send', 'Send Invitation'),
      onOk: async () => {
        setSendingEmployeeInvitation(prev => ({ ...prev, [appraisal.id]: true }))
        try {
          const res = await api.sendAppraisalEmployeeInvitation(appraisal.id)
          message.success(t('session.invitations.messages.sent', 'Employee invitation sent to {{email}}', { email: res.email }))
        } catch (e) {
          message.error(e.message || t('session.invitations.messages.failed', 'Unable to send employee invitation'))
        } finally {
          setSendingEmployeeInvitation(prev => ({ ...prev, [appraisal.id]: false }))
        }
      }
    })
  }

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields()
      confirmSaveSession(values.status, values)
    } catch (_) {
      // Ant Design already marks the invalid fields.
    }
  }

  const handleImport = async (file) => {
    // Ant Upload: returning false stops auto-upload
    setImportResult(null)
    setImportJobId(null)
    setImportUploadProgress(0)
    setImportProgress(0)
    setImporting(true)

    const allErrors = []
    try {
      // 1) Upload Excel once to create job (XHR upload progress)
      const job = await api.createSessionEmployeesExcelImportJob(id, file, {
        onProgress: setImportUploadProgress
      })
      setImportJobId(job.job_id)
      if (Array.isArray(job.parse_errors) && job.parse_errors.length) {
        allErrors.push(...job.parse_errors)
      }

      // 2) Process in small batches so UI can update progress
      const batchSize = 100
      let done = false
      let last = null
      while (!done) {
        // eslint-disable-next-line no-await-in-loop
        last = await api.processSessionEmployeesExcelImportJobBatch(id, job.job_id, { batchSize })
        setImportProgress(last.percent ?? 0)
        if (Array.isArray(last.errors_now) && last.errors_now.length) {
          allErrors.push(...last.errors_now)
        }
        done = Boolean(last.done)

        // Yield so the browser paints progress.
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => setTimeout(resolve, 0))
      }

      // Compose a result in roughly the same shape the UI expects.
      const result = {
        job_id: job.job_id,
        total_rows: last?.total_rows ?? job.total_rows,
        processed: last?.processed_total ?? 0,
        errors: allErrors,
        ...(last?.counters || {})
      }

      setImportResult(result)
      setImportProgress(100)
      message.success(t('session.import.messages.complete', 'Import complete'))

      const [updatedSession] = await Promise.all([
        api.getSession(id),
        loadAppraisalPage(1)
      ])
      setSession(updatedSession)
      setSelectedEmployeeIds(updatedSession.employees || [])
      window.dispatchEvent(new CustomEvent('dev-users.updated'))
    } catch (e) {
      message.error(e.message || t('session.import.messages.failed', 'Unable to import employees and approvers'))
      setImportResult({ error: e.message || t('session.import.messages.failed', 'Unable to import employees and approvers'), body: e.body })
    } finally {
      setImporting(false)
    }

    return false
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const { blob, filename } = await api.exportSessionExcel(id, session?.name)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      message.error(e.message || t('session.export.messages.failed', 'Failed to export session data'))
    } finally {
      setExporting(false)
    }
  }

  const handleDownloadImportExample = async () => {
    setDownloadingImportExample(true)
    try {
      const { blob, filename } = await api.downloadSessionImportExample(id, session?.name)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      message.error(e.message || t('session.import.messages.example_failed', 'Failed to download the import example'))
    } finally {
      setDownloadingImportExample(false)
    }
  }

  const handleAppraisalPdfExport = (appraisalId) => {
    window.open(api.getAppraisalPdfUrl(appraisalId), '_blank', 'noopener,noreferrer')
  }

  const handleBulkPdfExport = async () => {
    if (!selectedBulkIds.length) return
    setDownloadingBulkPdf(true)
    try {
      const { blob, filename } = await api.exportSessionBulkPdf(id, selectedBulkIds)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      setSelectedBulkIds([])
      setBulkActionType(null)
    } catch (e) {
      message.error(e.message || 'Could not download the selected PDFs')
    } finally {
      setDownloadingBulkPdf(false)
    }
  }

  const handleBulkAutoApprove = () => {
    if (!selectedBulkIds.length) return
    Modal.confirm({
      title: `Auto-approve ${selectedBulkIds.length} selected appraisal${selectedBulkIds.length === 1 ? '' : 's'}?`,
      content: 'This signs off all remaining assigned approval levels after Approver 1 and marks each eligible appraisal as completed.',
      okText: 'Auto-approve and complete',
      cancelText: 'Cancel',
      onOk: async () => {
        setAutoApproving(true)
        try {
          const result = await api.bulkAutoApproveSessionAppraisals(id, selectedBulkIds)
          setSelectedBulkIds([])
          setBulkActionType(null)
          await loadAppraisalPage(appraisalPage)
          if (result.auto_approved) {
            message.success(`${result.auto_approved} appraisal${result.auto_approved === 1 ? '' : 's'} completed.`)
          }
          if (result.skipped?.length) {
            message.warning(`${result.skipped.length} selected appraisal${result.skipped.length === 1 ? ' was' : 's were'} skipped because eligibility changed.`)
          }
        } catch (e) {
          message.error(e.message || 'Could not auto-approve the selected appraisals')
          throw e
        } finally {
          setAutoApproving(false)
        }
      }
    })
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
  if (error) return <Alert type='error' message={error} showIcon />
  if (!session) return <Alert type='error' message={t('session.not_found', 'Session not found')} showIcon />

  if (!isHR) {
    return (
      <Alert
        type='error'
        showIcon
        message={t('common.not_authorized', 'Not authorized')}
        description={t('session.hr_only', 'Only HR users can view sessions.')}
      />
    )
  }

  const counts = appraisalSummary.status_counts || {}
  const total = appraisalSummary.total || 0
  const completed = counts.completed || 0
  const selectedHrReviewerIds = Array.isArray(hrDelegateTo)
    ? hrDelegateTo
    : (hrDelegateTo ? [hrDelegateTo] : [])
  const departmentOptions = (appraisalSummary.departments || [])
    .map(department => ({ value: department, label: department }))
  const workflowStages = [
    { label: t('session.stages.employee', 'Employee'), detail: t('workflow.self_appraisal', 'Self-appraisal / review'), count: (counts.pending_employee || 0) + (counts.pending_employee_review || 0), color: 'var(--tti-warning)' },
    { label: t('session.stages.approver_1', 'Approver 1'), detail: t('workflow.review', 'Review'), count: counts.pending_manager || 0, color: 'var(--tti-info)' },
    { label: t('session.stages.approver_2', 'Approver 2'), detail: t('workflow.approval', 'Approval'), count: counts.pending_head || 0, color: '#722ed1' },
    { label: t('session.stages.approver_3', 'Approver 3'), detail: t('workflow.approval', 'Approval'), count: counts.pending_bu_head || 0, color: '#2f54eb' },
    { label: t('session.stages.approver_4', 'Approver 4'), detail: t('workflow.approval', 'Approval'), count: counts.pending_approver_4 || 0, color: '#0080c1' },
    { label: t('session.stages.approver_5', 'Approver 5'), detail: t('workflow.final_approval', 'Final approval'), count: counts.pending_approver_5 || 0, color: '#005a8d' }
  ]
  const deadlineDays = daysUntil(finalSessionDeadlineDate(session))
  const appraisalCols = [
    {
      title: t('appraisals.table.employee', 'Employee'), key: 'emp',
      render: (_, r) => (
        <span>
          {personLabel(r.employee_first_name, r.employee_last_name, r.employee_email, r.employee_position, r.session_template)}
          {r.employee_employment_status === 'resigned' && (
            <Tag color='red' style={{ marginLeft: 8 }}>{t('session.staff.employment_status_resigned', 'Resigned')}</Tag>
          )}
        </span>
      )
    },
    { title: t('appraisals.table.employee_id', 'Employee ID'), dataIndex: 'employee_company_id', key: 'employee_id', render: value => value || '—' },
    { title: t('appraisals.table.department', 'Department'), dataIndex: 'department_name', key: 'department', render: value => value || '—' },
    {
      title: t('appraisals.table.consent', 'Consent'),
      dataIndex: 'employee_consent',
      key: 'consent',
      width: 170,
      render: consent => {
        if (!consent) return '—'
        const colors = { accepted: 'green', proceeded: 'green', declined: 'red', pending: 'gold' }
        const labels = { accepted: 'Accepted', proceeded: 'Proceeded', declined: 'Declined', pending: 'Pending' }
        return (
          <div className='session-consent-status'>
            <Tag color={colors[consent.decision]} className='session-consent-status-tag'>{labels[consent.decision] || consent.decision}</Tag>
            {consent.responded_at && (
              <time className='session-consent-status-time' dateTime={consent.responded_at}>{new Date(consent.responded_at).toLocaleString()}</time>
            )}
          </div>
        )
      }
    },
    {
      title: t('appraisals.table.approver', 'Approver'),
      key: 'mgr',
      render: (_, r) => <DelegationTree appraisal={r} isHRSuperadmin={isHRSuperadmin} revokeFunctionalReview={revokeFunctionalReview} t={t} />
    },
    {
      title: t('appraisals.table.status', 'Status'),
      dataIndex: 'status',
      key: 'st',
      width: 280,
      render: (status, appraisal) => {
        const originalManager = appraisal.latest_delegation_from_manager_name
        return (
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: originalManager ? 6 : 0 }}>
              <StatusBadge status={status} label={appraisal.status_label} />
            </div>
            {originalManager && (
              <div className='session-original-manager'>
                <Text type='secondary' className='session-original-manager-arrow'>↳</Text>
                <Tag color='blue' className='session-original-manager-tag'>Original Manager: {delegationStatusPersonName(originalManager)}</Tag>
              </div>
            )}
          </div>
        )
      }
    },
    ...completedRatingColumns(appraisals),
    {
      title: '',
      key: 'a',
      width: 260,
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Tooltip title={t('appraisals.actions.view', 'View appraisal')}>
            <Link to={`/appraisals/${r.id}`}><Button size='small' icon={<EyeOutlined />} aria-label={t('appraisals.actions.view', 'View appraisal')} /></Link>
          </Tooltip>
          {isHRSuperadmin && session?.status === 'active' && r.status === 'pending_employee' && (
            <Tooltip title={t('session.invitations.actions.send', 'Send Employee Invitation')}>
              <Button
                size='small'
                icon={<MailOutlined />}
                aria-label={t('session.invitations.actions.send', 'Send Employee Invitation')}
                loading={Boolean(sendingEmployeeInvitation[r.id])}
                onClick={() => confirmSendEmployeeInvitation(r)}
              />
            </Tooltip>
          )}
          {r.status === 'completed' && (
            <Tooltip title={t('appraisals.actions.download_pdf', 'Download PDF')}>
              <Button size='small' icon={<FilePdfOutlined />} aria-label={t('appraisals.actions.download_pdf', 'Download PDF')} onClick={() => handleAppraisalPdfExport(r.id)} />
            </Tooltip>
          )}
          {isHRSuperadmin && session?.status === 'active' && (hrSupportsDirectDelegation || hrSupportsReviewDelegation) && ['pending_employee', 'pending_manager'].includes(r.status) && (
            <Tooltip title={t('delegation.actions.delegate_appraisal', 'Delegate appraisal')}>
              <Button size='small' icon={<UserSwitchOutlined />} aria-label={t('delegation.actions.delegate_appraisal', 'Delegate appraisal')} onClick={() => openHrDelegationModal(r)} />
            </Tooltip>
          )}
          {isHRSuperadmin && (
            <Tooltip title={t('appraisals.session.actions.edit_staff', 'Edit staff')}>
              <Button size='small' icon={<EditOutlined />} aria-label={t('appraisals.session.actions.edit_staff', 'Edit staff')} onClick={() => openEditStaffModal(r)} />
            </Tooltip>
          )}
          {isHRSuperadmin && (
            <Popconfirm
              title={t('appraisals.session.confirm.remove_employee', 'Remove employee from session?')}
              // description='This will delete the appraisal row for this employee in this session.'
              okText={t('common.remove', 'Remove')}
              okButtonProps={{ danger: true, className: 'tti-btn-danger' }}
              cancelText={t('common.cancel', 'Cancel')}
              onConfirm={async () => {
                try {
                  await api.removeSessionEmployee(id, r.employee)
                  message.success(t('appraisals.session.messages.removed', 'Removed'))
                  setSelectedEmployeeIds(prev => prev.filter(empId => empId !== r.employee))
                  await loadAppraisalPage(appraisals.length === 1 && appraisalPage > 1 ? appraisalPage - 1 : appraisalPage)
                } catch (e) {
                  message.error(e.message || t('appraisals.session.messages.remove_failed', 'Failed to remove'))
                }
              }}
            >
              <Tooltip title={t('appraisals.session.actions.remove_employee', 'Remove employee')}>
                <Button className='tti-btn-danger' size='small' icon={<DeleteOutlined />} aria-label={t('appraisals.session.actions.remove_employee', 'Remove employee')} />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      )
    }
  ]

  const reviewPeriodLabels = {
    employee: t('session.review_periods.employee', 'Employee Review Period'),
    manager: t('session.review_periods.approver_1', 'Approver 1 Review Period'),
    department_head: t('session.review_periods.approver_2', 'Approver 2 Review Period'),
    bu_head: t('session.review_periods.approver_3', 'Approver 3 Review Period'),
    approver_4: t('session.review_periods.approver_4', 'Approver 4 Review Period'),
    approver_5: t('session.review_periods.approver_5', 'Approver 5 Review Period')
  }

  const reviewPeriodCols = [
    { title: t('session.review_periods.stage', 'Stage'), dataIndex: 'label', key: 'label', width: 190, render: (_, record) => reviewPeriodLabels[record.stage] || record.label },
    {
      title: <span>{t('common.start_with_format', 'Start ({{format}})', { format: DATE_FORMAT_LABEL })} <Text type='danger'>*</Text></span>,
      dataIndex: 'start_date',
      key: 'start_date',
      width: 180,
      render: (_, record) => (
        <DatePicker
          disabled={!isHRSuperadmin || periodSaving[record.id]}
          format='YYYY-MM-DD'
          value={record.start_date ? dayjs(record.start_date) : null}
          onChange={(_, dateString) => updateReviewPeriodLocal(record.id, { start_date: dateString || null })}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: <span>{t('common.end_with_format', 'End ({{format}})', { format: DATE_FORMAT_LABEL })} <Text type='danger'>*</Text></span>,
      dataIndex: 'end_date',
      key: 'end_date',
      width: 180,
      render: (_, record) => (
        <DatePicker
          disabled={!isHRSuperadmin || periodSaving[record.id]}
          format='YYYY-MM-DD'
          value={record.end_date ? dayjs(record.end_date) : null}
          onChange={(_, dateString) => updateReviewPeriodLocal(record.id, { end_date: dateString || null })}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: t('session.review_periods.deadline_type', 'Deadline Type'),
      dataIndex: 'deadline_type',
      key: 'deadline_type',
      width: 150,
      render: (_, record) => (
        <Switch
          disabled={!isHRSuperadmin || periodSaving[record.id]}
          checked={record.deadline_type === 'hard'}
          checkedChildren={t('session.deadlines.hard_label', 'Hard')}
          unCheckedChildren={t('session.deadlines.soft_label', 'Soft')}
          onChange={checked => updateReviewPeriodLocal(record.id, { deadline_type: checked ? 'hard' : 'soft' })}
          style={{ minWidth: 64 }}
        />
      )
    },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const missingDates = !record.start_date || !record.end_date
        return (
          <Tooltip title={t('session.review_periods.actions.save', 'Save review period')}>
            <Button
              className='tti-btn-primary-grey'
              size='small'
              icon={<SaveOutlined />}
              aria-label={t('session.review_periods.actions.save', 'Save review period')}
              disabled={!isHRSuperadmin || missingDates}
              loading={Boolean(periodSaving[record.id])}
              onClick={() => saveReviewPeriod(record)}
            />
          </Tooltip>
        )
      }
    }
  ]

  return (
    <div ref={pageTopRef} style={{ maxWidth: 1600, margin: '0 auto', padding: 20, transition: 'box-shadow 0.3s', boxShadow: justSaved ? '0 0 0 3px rgba(82, 196, 26, 0.35)' : 'none' }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} aria-label={t('common.back', 'Back')} onClick={() => navigate(-1)} />
        </Col>
        <Col flex='auto'>
          {session?.name && <Title level={3} className='tti-page-title' style={{ margin: 0 }}>{session.name}</Title>}
          <Text type='secondary' style={{ fontSize: 16 }}>{t('session.details', 'Session Details')}</Text>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={changeTab}
        tabBarStyle={session?.read_only_report_type && !isHRSuperadmin ? { display: 'none' } : undefined}
        items={[
          {
            key: 'appraisals',
            label: t('appraisals.title', 'Appraisals'),
            children: session?.read_only_report_type
              ? <ReadOnlySessionReport sessionId={id} canManage={isHRSuperadmin} refreshKey={tabRefreshKey} />
              : (
                <>
                  {!employeeListHidden && isHRSuperadmin && total === 0 && (
                    <Alert
                      type='info'
                      showIcon
                      message={t('session.appraisals.add_to_start', 'Add employees to start this session')}
                      description={t('session.appraisals.import_help', 'Import your employee and approver list from Excel to set up the session in one step.')}
                      action={(
                        <div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                              type='primary'
                              size='large'
                              icon={<UploadOutlined />}
                              onClick={() => setImportModalOpen(true)}
                            >
                              {t('appraisals.session.actions.import_excel', 'Import Excel')}
                            </Button>
                            <Button size='large' onClick={openAddStaffModal}>{t('appraisals.session.actions.add_manually', 'Add Manually')}</Button>
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <Button
                              type='link'
                              size='small'
                              icon={<DownloadOutlined />}
                              loading={downloadingImportExample}
                              onClick={handleDownloadImportExample}
                              style={{ padding: 0 }}
                            >
                              {t('session.import.actions.download_example', 'Download Example Employee Import Excel Template')}
                            </Button>
                          </div>
                        </div>
                      )}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  <AppraisalWorkflowCard
                    total={total}
                    completed={completed}
                    secondaryValue={deadlineDays !== null ? Math.abs(deadlineDays) : null}
                    secondaryLabel={deadlineDays !== null && deadlineDays < 0 ? t('session.deadlines.days_overdue', 'Days overdue') : t('session.deadlines.days_remaining', 'Days remaining')}
                    secondaryUrgent={deadlineDays !== null && deadlineDays <= 7}
                    workflowStages={workflowStages}
                  />

                  <RatingDistributionChart sessionId={id} />

                  {!employeeListHidden && <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      {isHRSuperadmin && <Button type='primary' icon={<UploadOutlined />} onClick={() => setImportModalOpen(true)}>{t('appraisals.session.actions.import_excel', 'Import Excel')}</Button>}
                      {isHRSuperadmin && <Button onClick={openAddStaffModal}>{t('appraisals.session.actions.add_employees', 'Add Employees')}</Button>}
                      <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>{t('appraisals.session.actions.export', 'Export Data')}</Button>
                    </div>
                    {isHRSuperadmin && (
                      <div style={{ textAlign: 'right', marginTop: 4 }}>
                        <Button
                          type='link'
                          size='small'
                          icon={<DownloadOutlined />}
                          loading={downloadingImportExample}
                          onClick={handleDownloadImportExample}
                          style={{ padding: 0 }}
                        >
                          {t('session.import.actions.download_example', 'Download Example Employee Import Excel Template')}
                        </Button>
                      </div>
                    )}
                  </div>}

                  {!employeeListHidden && <div ref={appraisalsRef}>
                    <Card size='small'>
                      <Title level={4} style={{ marginBottom: 12 }}>
                        {t('appraisals.title', 'Appraisals')}
                      </Title>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        <Input
                          allowClear
                          placeholder={t('session.appraisals.search_placeholder', 'Search by employee name, ID, or email')}
                          value={appraisalSearch}
                          onChange={event => setAppraisalSearch(event.target.value)}
                          style={{ width: 320 }}
                        />
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp='label'
                          placeholder={t('session.appraisals.department_filter', 'Filter by department')}
                          value={appraisalDepartment}
                          onChange={value => {
                            setAppraisalDepartment(value)
                            setAppraisalPage(1)
                          }}
                          options={departmentOptions}
                          style={{ width: 260 }}
                        />
                        <Select
                          allowClear
                          placeholder={t('session.appraisals.status_filter', 'Filter by status')}
                          value={appraisalStatus}
                          onChange={value => {
                            setAppraisalStatus(value)
                            setAppraisalPage(1)
                          }}
                          options={[
                            { value: 'pending_employee', label: t('appraisal.status.pending_employee', 'Pending Employee') },
                            { value: 'pending_employee_review', label: t('appraisal.status.pending_employee_review', 'Pending Employee Review') },
                            { value: 'pending_manager', label: t('appraisal.status.pending_manager', 'Awaiting Approver 1 approval') },
                            { value: 'pending_head', label: t('appraisal.status.pending_head', 'Awaiting Approver 2 approval') },
                            { value: 'pending_bu_head', label: t('appraisal.status.pending_bu_head', 'Awaiting Approver 3 approval') },
                            { value: 'pending_approver_4', label: t('appraisal.status.pending_approver_4', 'Awaiting Approver 4 approval') },
                            { value: 'pending_approver_5', label: t('appraisal.status.pending_approver_5', 'Awaiting Approver 5 approval') },
                            { value: 'completed', label: t('appraisal.status.completed', 'Completed') }
                          ]}
                          style={{ width: 260 }}
                        />
                      </div>
                      {selectedBulkIds.length > 0 && (
                        <div className='session-bulk-action-bar'>
                          <div className='session-bulk-action-summary'>
                            <Text strong>{selectedBulkIds.length} selected</Text>
                            <Text type='secondary'>
                              {bulkActionType === 'auto-approve'
                                ? 'Manager-approved appraisals ready for completion'
                                : 'Completed appraisals ready for PDF download'}
                            </Text>
                          </div>
                          <Space wrap>
                            {bulkActionType === 'auto-approve' ? (
                              <Button type='primary' icon={<CheckCircleOutlined />} loading={autoApproving}
                                onClick={handleBulkAutoApprove}>Auto-approve and complete</Button>
                            ) : (
                              <Button type='primary' icon={<DownloadOutlined />} loading={downloadingBulkPdf}
                                onClick={handleBulkPdfExport}>Download PDFs</Button>
                            )}
                            <Button icon={<CloseOutlined />} onClick={() => {
                              setSelectedBulkIds([])
                              setBulkActionType(null)
                            }}>Clear selection</Button>
                          </Space>
                        </div>
                      )}
                      <Table
                        dataSource={appraisals}
                        rowSelection={{
                          selectedRowKeys: selectedBulkIds,
                          preserveSelectedRowKeys: true,
                          getTitleCheckboxProps: () => ({ disabled: !bulkActionType }),
                          onChange: (keys, rows) => {
                            if (!keys.length) {
                              setSelectedBulkIds([])
                              setBulkActionType(null)
                              return
                            }
                            const nextActionType = bulkActionType || bulkActionForAppraisal(rows[0])
                            if (nextActionType === 'auto-approve') {
                              if (keys.length > 200) return message.warning('Select at most 200 appraisals per auto-approval.')
                            } else {
                              if (keys.length > 100) return message.warning('Select at most 100 completed appraisals per download.')
                            }
                            setBulkActionType(nextActionType)
                            setSelectedBulkIds(keys)
                          },
                          getCheckboxProps: record => ({
                            disabled: !bulkActionForAppraisal(record)
                              || (bulkActionType && bulkActionForAppraisal(record) !== bulkActionType)
                          })
                        }}
                        columns={appraisalCols}
                        rowKey='id'
                        size='small'
                        loading={loading || tabLoading.appraisals || appraisalPageLoading}
                        pagination={{
                          current: appraisalPage,
                          pageSize: 30,
                          total: appraisalTotal,
                          showSizeChanger: false,
                          onChange: page => {
                            setAppraisalPageLoading(true)
                            setAppraisalPage(page)
                            appraisalsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          }
                        }}
                        locale={{ emptyText: t('session.appraisals.empty', 'No appraisals found for this session') }}
                      />
                    </Card>
                  </div>}
                </>
              )
          },
          {
            key: 'review-periods',
            label: t('session.review_periods.title', 'Review Periods'),
            children: (
              <Card
                size='small'
              >
                <Title level={4} style={{ marginBottom: 12 }}>
                  {t('session.review_periods.title', 'Review Periods')}
                </Title>
                <Table
                  dataSource={visibleReviewPeriods}
                  columns={reviewPeriodCols}
                  rowKey='id'
                  size='small'
                  pagination={false}
                  loading={loading || tabLoading['review-periods']}
                  tableLayout='fixed'
                  style={{ maxWidth: 800 }}
                />
              </Card>
            )
          },
          {
            key: 'emails',
            label: t('session.emails.title', 'Session Emails'),
            children: (
              <Spin spinning={Boolean(tabLoading.emails)}>
                <div>
                  <Card
                    size='small'
                    style={{ marginBottom: 16 }}
                  >
                    <Title level={4} style={{ marginBottom: 12 }}>
                      {t('session.emails.title', 'Session Emails')}
                    </Title>

                    <Alert
                      type='info'
                      showIcon
                      style={{ marginBottom: 16 }}
                      message={t('session.emails.available_placeholders', 'Available placeholders')}
                      description={
                        <div>
                          <Text type='secondary'>
                            {t('session.emails.placeholders_help', 'Use placeholders such as {{examples}}.', { examples: '{{ recipient.first_name }}, {{ employee.full_name }}, {{ manager.full_name }}, {{ session.name }}, {{ session.end_date }}, {{ template.name }}, and {{ action_url }}' })}
                          </Text>
                        </div>
                      }
                    />

                    {isHRSuperadmin && (
                      <Button className='tti-btn-primary-grey' loading={sendingAllTestEmails} onClick={confirmSendAllTestEmails}>
                        {t('session.emails.actions.test_all', 'Test All Emails')}
                      </Button>
                    )}
                  </Card>
                  <Collapse
                    className='session-email-groups'
                    bordered={false}
                    expandIconPlacement='end'
                    expandIcon={({ isActive }) => (isActive ? <UpOutlined /> : <DownOutlined />)}
                    items={EMAIL_TEMPLATE_GROUPS.map(group => {
                      const groupedTemplates = emailTemplates.filter(template => group.types.includes(template.email_type))
                      return {
                        key: group.titleKey,
                        label: t(group.titleKey, group.titleFallback),
                        children: (
                          <>
                            <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>{t(group.descriptionKey, group.descriptionFallback)}</Text>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                              {groupedTemplates.map(template => (
                                <Card
                                  key={template.id}
                                  size='small'
                                  style={{ width: '100%', border: '1px solid var(--neutral-grey2)' }}
                                >


                                  {
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                      <Title level={4} style={{ margin: 0 }}>
                                        {emailTemplateTitle(template, t)}
                                      </Title>
                                      {isHRSuperadmin && (
                                        <Space size='small'>
                                          <Button
                                            size='small'
                                            loading={sendingTemplateTestEmail[template.id]}
                                            onClick={() => confirmSendTemplateTestEmail(template)}
                                          >
                                            {t('session.emails.actions.test', 'Test Email')}
                                          </Button>
                                          <Button
                                            className='tti-btn-primary-grey'
                                            size='small'
                                            loading={emailTemplateSaving[template.id]}
                                            onClick={() => saveEmailTemplate(template)}
                                          >
                                            {t('common.save', 'Save')}
                                          </Button>
                                        </Space>
                                      )}
                                    </div>
                                  }
                                  <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>{template.description}</Text>
                                  <Form layout='vertical' size='small'>
                                    <Form.Item label={t('session.emails.subject', 'Subject')} style={{ marginBottom: 12 }}>
                                      <Input
                                        disabled={!isHRSuperadmin}
                                        value={template.subject_template}
                                        onChange={e => updateEmailTemplateLocal(template.id, { subject_template: e.target.value })}
                                      />
                                    </Form.Item>
                                    <Form.Item label={t('session.emails.body', 'Body')} style={{ marginBottom: 0 }}>
                                      <EmailTemplateEditor
                                        sessionId={id}
                                        disabled={!isHRSuperadmin}
                                        value={template.body_template}
                                        onChange={value => updateEmailTemplateLocal(template.id, { body_template: value })}
                                      />
                                    </Form.Item>
                                  </Form>
                                </Card>
                              ))}
                            </div>
                          </>
                        )
                      }
                    }).filter((_, index) => emailTemplates.some(template => EMAIL_TEMPLATE_GROUPS[index].types.includes(template.email_type)))}
                  />
                </div>
              </Spin>
            )
          },
          {
            key: 'properties',
            label: t('session.properties.title', 'Session Properties'),
            children: (
              <Card size='small' loading={Boolean(tabLoading.properties)}>
                <Title level={4}>{t('session.properties.title', 'Session Properties')}</Title>
                <Form form={form} layout='vertical' size='small' style={{ maxWidth: 720 }}>
                  <Form.Item name='name' label={t('session.fields.name', 'Session Name')} rules={[{ required: true }]} style={{ marginBottom: 12 }}>
                    <Input disabled={!isHRSuperadmin} />
                  </Form.Item>

                  <Form.Item
                    name='display_title'
                    label={t('session.fields.display_title', 'Display Title')}
                    help={t('session.fields.display_title_help', 'Used as the title on appraisal forms and PDF exports.')}
                    rules={[{ required: true }]}
                    style={{ marginBottom: 28 }}
                  >
                    <Input disabled={!isHRSuperadmin} />
                  </Form.Item>

                  <Form.Item name='template' label={t('session.fields.template', 'Template')} rules={[{ required: true }]} style={{ marginBottom: 12 }}>
                    <Select
                      disabled={!isHRSuperadmin}
                      options={templates.map(template => ({ value: template.code, label: template.name }))}
                      onChange={async value => {
                        const selectedTemplate = templates.find(template => template.code === value)
                        try {
                          setBusinessUnits(await api.getBusinessUnitOptions({
                            region: session.region,
                            organization: selectedTemplate?.organization
                          }))
                        } catch (e) {
                          setBusinessUnits([])
                          message.error(e.message || t('organization.messages.business_units_load_failed', 'Unable to load business units'))
                        }
                      }}
                    />
                  </Form.Item>

                  <Form.Item name='description' label={t('session.fields.description', 'Description')} style={{ marginBottom: 12 }}>
                    <TextArea rows={2} disabled={!isHRSuperadmin} />
                  </Form.Item>

                  <Card
                    size='small'
                    style={{ marginBottom: 12 }}
                  >
                    <Title level={4}>{t('session.performance_period.title', 'Employee Performance Period')}</Title>
                    <Text type='secondary'>{t('session.performance_period.help', 'Default period displayed on appraisals in this session.')}</Text>
                    <Row gutter={12} style={{ marginTop: 8 }}>
                      <Col span={12}>
                        <Form.Item
                          name='performance_period_start_date'
                          label={t('common.start_with_format', 'Start ({{format}})', { format: DATE_FORMAT_LABEL })}
                          rules={[{ required: true, message: t('session.performance_period.validation.start_required', 'Performance period start is required') }]}
                        >
                          <DatePicker format='YYYY-MM-DD' disabled={!isHRSuperadmin} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name='performance_period_end_date'
                          label={t('common.end_with_format', 'End ({{format}})', { format: DATE_FORMAT_LABEL })}
                          dependencies={['performance_period_start_date']}
                          rules={[
                            { required: true, message: t('session.performance_period.validation.end_required', 'Performance period end is required') },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const start = getFieldValue('performance_period_start_date')
                                return !start || !value || !value.isBefore(start)
                                  ? Promise.resolve()
                                  : Promise.reject(new Error(t('session.performance_period.validation.order', 'End date cannot be before start date')))
                              }
                            })
                          ]}
                        >
                          <DatePicker format='YYYY-MM-DD' disabled={!isHRSuperadmin} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  <Form.Item label={t('delegation.title', 'Delegation')} style={{ marginBottom: 12 }}>
                    <Form.Item name='manager_delegation_options' noStyle>
                      <Checkbox.Group
                        disabled={!isHRSuperadmin}
                        options={[
                          { value: 'direct_transfer', label: t('delegation.type.direct', 'Direct delegation') },
                          { value: 'functional_review', label: t('delegation.type.indirect', 'Indirect delegation') }
                        ]}
                        style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                      />
                    </Form.Item>
                    {selectedTemplateCode === 'vietnam_milwaukee' && (
                      <Form.Item
                        name='rating_justification_min_length'
                        label='Minimum Rating Justification Length'
                        rules={[{ required: true, message: 'Enter the minimum justification length.' }]}
                        style={{ marginTop: 12, marginBottom: 12 }}
                      >
                        <InputNumber min={1} max={1000} disabled={!isHRSuperadmin} style={{ width: '100%' }} />
                      </Form.Item>
                    )}
                    <div><Text type='secondary'>{t('delegation.disabled_help', 'Leave both unchecked to disable delegation for this session.')}</Text></div>
                  </Form.Item>
                  <Form.Item name='status' label={t('common.status', 'Status')} rules={[{ required: true }]} style={{ marginBottom: 12 }}>
                    <Select
                      disabled={!isHRSuperadmin}
                      options={[
                        { value: 'draft', label: t('dashboard.status.draft', 'Draft') },
                        { value: 'active', label: t('dashboard.status.active', 'Active') },
                        { value: 'closed', label: t('dashboard.status.closed', 'Closed') }
                      ]}
                    />
                  </Form.Item>

                  {isHRSuperadmin && (
                    <Button icon={<SaveOutlined />} loading={saving} onClick={handleSaveClick} className='tti-btn-primary-grey'>
                      {t('common.save_changes', 'Save Changes')}
                    </Button>
                  )}
                </Form>
              </Card>
            )
          },
          {
            key: 'hr-users',
            label: t('session.hr_users.title', 'HR Users'),
            children: <SessionHRUserManager sessionId={id} refreshKey={tabRefreshKey} />
          },
          {
            key: 'imported-report',
            label: 'Imported Performance Report',
            children: <ReadOnlySessionReport sessionId={id} canManage={isHRSuperadmin} mode='import' refreshKey={tabRefreshKey} />
          }
        ].filter(item => allowedSessionTabs.includes(item.key))}
      />

      <Modal
        title={delegationAppraisal
          ? t('delegation.modal.title_for_employee', 'Delegate appraisal for {{employee}}', { employee: personLabel(delegationAppraisal.employee_first_name, delegationAppraisal.employee_last_name, delegationAppraisal.employee_email, '', delegationAppraisal.session_template) })
          : t('delegation.modal.title', 'Delegate appraisal')}
        open={Boolean(delegationAppraisal)}
        onCancel={closeHrDelegationModal}
        onOk={submitHrDelegation}
        okText={hrDelegationMode === 'functional_review' ? t('delegation.actions.request_feedback', 'Request Feedback') : t('delegation.actions.transfer_approver', 'Transfer Approver')}
        okButtonProps={{
          loading: hrDelegating,
          disabled: hrDelegationLoading || (Array.isArray(hrDelegateTo) ? hrDelegateTo.length === 0 : !hrDelegateTo)
        }}
      >
        <Form layout='vertical'>
          <Form.Item label={t('delegation.fields.type', 'Delegation type')}>
            <Select
              value={hrDelegationMode}
              onChange={changeHrDelegationMode}
              options={[
                hrSupportsReviewDelegation && { value: 'functional_review', label: t('delegation.type.indirect_feedback', 'Indirect - request reviewer feedback') },
                hrSupportsDirectDelegation && { value: 'direct_transfer', label: t('delegation.type.direct_ownership', 'Direct - transfer Approver 1 ownership') }
              ].filter(Boolean)}
            />
          </Form.Item>
          <Text type='secondary'>
            {hrDelegationMode === 'functional_review'
              ? t('delegation.modal.indirect_help', 'Choose one or more users participating in an active session. The assigned Approver 1 remains unchanged.')
              : t('delegation.modal.direct_help', 'Choose an eligible Approver 1 participating in this session. They will become responsible for the appraisal.')}
          </Text>
          {hrDelegationError && <Alert type='error' showIcon message={hrDelegationError} style={{ marginTop: 16 }} />}
          <Form.Item
            label={hrDelegationMode === 'functional_review'
              ? t('delegation.fields.selected_reviewers', 'Selected Reviewers')
              : t('delegation.fields.selected_reviewer', 'Selected Reviewer')}
            style={{ marginTop: 16 }}
          >
            <div style={{ minHeight: 40, padding: 8, border: '1px solid #d9d9d9', borderRadius: 6 }}>
              {selectedHrReviewerIds.length > 0
                ? selectedHrReviewerIds.map(id => {
                  const candidate = hrDelegateCandidates.find(item => item.id === id)
                  return (
                    <Tag
                      key={id}
                      closable
                      onClose={() => setHrDelegateTo(current => hrDelegationMode === 'functional_review'
                        ? (Array.isArray(current) ? current : []).filter(value => value !== id)
                        : null)}
                    >
                      {candidate ? delegationCandidateLabel(candidate) : t('delegation.fields.selected_reviewer', 'Selected Reviewer')}
                    </Tag>
                  )
                })
                : <Text type='secondary'>{t('delegation.empty.no_reviewer_selected', 'No reviewer selected')}</Text>}
            </div>
          </Form.Item>
          <Button
            type='dashed'
            block
            icon={<UserAddOutlined />}
            onClick={openHrAddCandidate}
          >
            {t('delegation.actions.add_reviewer', 'Add Reviewer')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t('delegation.add_reviewer.title', 'Add Reviewer')}
        open={hrAddCandidateOpen}
        onCancel={() => {
          setHrAddCandidateOpen(false)
          setHrExistingCandidateToAdd(null)
        }}
        onOk={confirmHrAddCandidate}
        okText={hrExistingCandidateToAdd
          ? t('delegation.actions.select_reviewer', 'Select Reviewer')
          : t('delegation.actions.create_select_reviewer', 'Create and Select Reviewer')}
        okButtonProps={{
          className: 'tti-btn-primary-grey',
          loading: hrCreatingCandidate,
          disabled: !hrExistingCandidateToAdd && (!hrNewCandidate.email.trim() || !hrNewCandidate.first_name.trim() || !hrNewCandidate.last_name.trim())
        }}
        cancelButtonProps={{ className: 'tti-btn-secondary' }}
      >
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <Text strong>{t('delegation.fields.existing_reviewer', 'Select an Existing Reviewer')}</Text>
          <Select
            allowClear
            showSearch
            style={{ width: '100%' }}
            placeholder={hrDelegationLoading
              ? t('delegation.assignment.loading_candidates', 'Loading reviewers...')
              : t('delegation.fields.search_reviewers', 'Search existing reviewers')}
            loading={hrDelegationLoading}
            value={hrExistingCandidateToAdd}
            onChange={setHrExistingCandidateToAdd}
            options={hrDelegateCandidates
              .filter(candidate => !selectedHrReviewerIds.includes(candidate.id))
              .map(candidate => ({
                value: candidate.id,
                label: delegationCandidateLabel(candidate),
                email: candidate.email
              }))}
            filterOption={(input, option) => `${option?.label || ''} ${option?.email || ''}`.toLowerCase().includes(input.toLowerCase())}
          />
          <Divider plain>{t('delegation.add_reviewer.or_create', 'or create a new reviewer')}</Divider>
          <Input
            type='email'
            placeholder={t('delegation.add_reviewer.email', 'Reviewer email')}
            disabled={Boolean(hrExistingCandidateToAdd)}
            value={hrNewCandidate.email}
            onChange={event => setHrNewCandidate(current => ({ ...current, email: event.target.value }))}
          />
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder={t('delegation.add_manager.first_name', 'First name')}
              disabled={Boolean(hrExistingCandidateToAdd)}
              value={hrNewCandidate.first_name}
              onChange={event => setHrNewCandidate(current => ({ ...current, first_name: event.target.value }))}
            />
            <Input
              placeholder={t('delegation.add_manager.last_name', 'Last name')}
              disabled={Boolean(hrExistingCandidateToAdd)}
              value={hrNewCandidate.last_name}
              onChange={event => setHrNewCandidate(current => ({ ...current, last_name: event.target.value }))}
            />
          </Space.Compact>
          <Input
            placeholder={t('delegation.add_manager.position_optional', 'Position (optional)')}
            disabled={Boolean(hrExistingCandidateToAdd)}
            value={hrNewCandidate.position}
            onChange={event => setHrNewCandidate(current => ({ ...current, position: event.target.value }))}
          />
        </Space>
      </Modal>

      <Modal
        title={t('session.emails.actions.send_tests', 'Send Test Emails')}
        open={testEmailModalOpen}
        onCancel={() => {
          setTestEmailModalOpen(false)
          setTestEmail('')
        }}
        onOk={sendTestEmails}
        okText={t('session.emails.actions.send_tests', 'Send Test Emails')}
        okButtonProps={{ loading: sendingTestEmail, disabled: !testEmail.trim() }}
      >
        <Text type='secondary'>
          {t('session.emails.test_address_help', 'Sends representative process and appraisal invite emails for this session to the test address only.')}
        </Text>
        <Input
          style={{ marginTop: 12 }}
          value={testEmail}
          onChange={e => setTestEmail(e.target.value)}
          placeholder={t('session.emails.test_email_placeholder', 'test@example.com')}
        />
      </Modal>

      <Modal
        title={editingAppraisal ? t('session.staff.edit_title', 'Edit Appraisal Staff') : t('appraisals.session.actions.add_employees', 'Add Employees')}
        open={employeeModalOpen && !employeeListHidden}
        maskClosable={false}
        onCancel={closeEmployeeModal}
        footer={null}
      >
        <div style={{ marginBottom: 20 }}>
          <Text strong>{editingAppraisal ? t('session.staff.edit_help', 'Edit employee and approval routing for this session') : t('session.staff.add_title', 'Add New Employee')}</Text>
          <Form form={addEmployeeForm} layout='vertical' style={{ marginTop: 12 }}>
            <Form.Item
              name='employee_email'
              label={t('session.staff.employee_email', 'Employee Email')}
              rules={[{ required: true, message: t('validation.email_required', 'Email is required') }, { type: 'email', message: t('validation.invalid_email', 'Invalid email') }]}
            >
              <AutoComplete
                placeholder={t('session.staff.search_employee', 'Search existing employee or type email')}
                options={staffOptions(staffSearchResults.employee)}
                filterOption={false}
                onSearch={value => searchStaff('employee', value)}
                onSelect={(_, option) => selectStaff('employee', option.user)}
              />
            </Form.Item>

            <Form.Item
              name='employee_first_name'
              label={t('session.staff.employee_first_name', 'Employee First Name')}
              rules={[{ required: true, message: t('session.staff.validation.employee_first_name', 'Employee first name is required') }]}
            >
              <Input placeholder={t('common.first_name', 'First name')} />
            </Form.Item>

            <Form.Item
              name='employee_last_name'
              label={t('session.staff.employee_last_name', 'Employee Last Name')}
              rules={[{ required: true, message: t('session.staff.validation.employee_last_name', 'Employee last name is required') }]}
            >
              <Input placeholder={t('common.last_name', 'Last name')} />
            </Form.Item>

            <Form.Item
              name='employee_company_id'
              label={t('session.staff.employee_id', 'Employee ID')}
              rules={[{ required: true, message: t('session.staff.validation.employee_id', 'Employee ID is required') }]}
            >
              <Input placeholder={t('session.staff.company_employee_id', 'Company employee ID')} />
            </Form.Item>

            <Form.Item
              name='employee_date_joined'
              label={t('session.staff.joined_date', 'Joined Date ({{format}})', { format: DATE_FORMAT_LABEL })}
            >
              <DatePicker format='YYYY-MM-DD' placeholder={DATE_FORMAT_LABEL} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name='employee_employment_status'
              label={t('session.staff.employment_status', 'Employment Status')}
              initialValue='active'
            >
              <Select
                options={[
                  { value: 'active', label: t('session.staff.employment_status_active', 'Active') },
                  { value: 'resigned', label: t('session.staff.employment_status_resigned', 'Resigned') }
                ]}
              />
            </Form.Item>

            <Form.Item
              name='employee_position'
              label={t('session.staff.employee_position', 'Employee Position')}
            >
              <Input placeholder={t('session.staff.position_placeholder', 'Position / job title')} />
            </Form.Item>

            <Form.Item name='business_unit_id' hidden><Input /></Form.Item>
            <Form.Item
              name='business_unit_name'
              label={t('session.staff.employee_business_unit_name', 'Employee Business Unit Name')}
            >
              <AutoComplete
                allowClear
                options={businessUnits.map(unit => ({
                  value: unit.name,
                  label: unit.name,
                  unit
                }))}
                placeholder={t('session.staff.business_unit_placeholder', 'Select an existing business unit or enter a new one')}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                onChange={value => {
                  const unit = businessUnits.find(
                    item => item.name.toLowerCase() === (value || '').trim().toLowerCase()
                  )
                  addEmployeeForm.setFieldsValue({
                    business_unit_id: unit?.id,
                    department_name: undefined
                  })
                }}
                onSelect={(_, option) => {
                  addEmployeeForm.setFieldsValue({
                    business_unit_id: option.unit.id,
                    department_name: undefined
                  })
                }}
              />
            </Form.Item>

            <Form.Item
              name='department_name'
              label={t('session.staff.employee_department_name', 'Employee Department Name')}
            >
              <AutoComplete
                allowClear
                placeholder={t('session.staff.department_placeholder', 'Select an existing department or enter a new one')}
                options={departments
                  .filter(department =>
                    (!selectedEmployeeBusinessUnit && !selectedEmployeeBusinessUnitName) ||
                    department.business_unit === selectedEmployeeBusinessUnit
                  )
                  .map(department => ({
                    value: department.name,
                    label: department.name,
                    department
                  }))}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                onSelect={selectDepartment}
              />
            </Form.Item>

            <Form.Item
              name='manager_email'
              label={t('session.staff.approver_email', 'Approver {{level}} Email', { level: 1 })}
              rules={[{ required: true, message: t('session.staff.validation.approver_email', 'Approver {{level}} email is required', { level: 1 }) }, { type: 'email', message: t('validation.invalid_email', 'Invalid email') }]}
            >
              <AutoComplete
                placeholder={t('session.staff.search_approver', 'Search existing approver {{level}} or type email', { level: 1 })}
                options={staffOptions(staffSearchResults.manager)}
                filterOption={false}
                onSearch={value => searchStaff('manager', value)}
                onSelect={(_, option) => selectStaff('manager', option.user)}
              />
            </Form.Item>

            <Form.Item
              name='manager_first_name'
              label={t('session.staff.approver_first_name', 'Approver {{level}} First Name', { level: 1 })}
              rules={[{ required: true, message: t('session.staff.validation.approver_first_name', 'Approver {{level}} first name is required', { level: 1 }) }]}
            >
              <Input placeholder={t('common.first_name', 'First name')} />
            </Form.Item>

            <Form.Item
              name='manager_last_name'
              label={t('session.staff.approver_last_name', 'Approver {{level}} Last Name', { level: 1 })}
              rules={[{ required: true, message: t('session.staff.validation.approver_last_name', 'Approver {{level}} last name is required', { level: 1 }) }]}
            >
              <Input placeholder={t('common.last_name', 'Last name')} />
            </Form.Item>

            <Form.Item
              name='department_head_email'
              label={t('session.staff.approver_email_optional', 'Approver {{level}} Email (Optional)', { level: 2 })}
              rules={[{ type: 'email', message: t('validation.invalid_email', 'Invalid email') }]}
            >
              <AutoComplete
                placeholder={t('session.staff.search_approver', 'Search existing approver {{level}} or type email', { level: 2 })}
                options={staffOptions(staffSearchResults.department_head)}
                filterOption={false}
                onSearch={value => searchStaff('department_head', value)}
                onSelect={(_, option) => selectStaff('department_head', option.user)}
              />
            </Form.Item>

            <Form.Item
              name='department_head_first_name'
              label={t('session.staff.approver_first_name', 'Approver {{level}} First Name', { level: 2 })}
              dependencies={['department_head_email']}
              rules={[({ getFieldValue }) => ({
                validator: (_, value) => !getFieldValue('department_head_email') || value ? Promise.resolve() : Promise.reject(new Error(t('session.staff.validation.approver_first_name', 'Approver {{level}} first name is required', { level: 2 })))
              })]}
            >
              <Input placeholder={t('common.first_name', 'First name')} />
            </Form.Item>

            <Form.Item
              name='department_head_last_name'
              label={t('session.staff.approver_last_name', 'Approver {{level}} Last Name', { level: 2 })}
              dependencies={['department_head_email']}
              rules={[({ getFieldValue }) => ({
                validator: (_, value) => !getFieldValue('department_head_email') || value ? Promise.resolve() : Promise.reject(new Error(t('session.staff.validation.approver_last_name', 'Approver {{level}} last name is required', { level: 2 })))
              })]}
            >
              <Input placeholder={t('common.last_name', 'Last name')} />
            </Form.Item>

            <Form.Item
              name='bu_head_email'
              label={t('session.staff.approver_email_optional', 'Approver {{level}} Email (Optional)', { level: 3 })}
              rules={[{ type: 'email', message: t('validation.invalid_email', 'Invalid email') }]}
            >
              <AutoComplete
                placeholder={t('session.staff.search_approver', 'Search existing approver {{level}} or type email', { level: 3 })}
                options={staffOptions(staffSearchResults.bu_head)}
                filterOption={false}
                onSearch={value => searchStaff('bu_head', value)}
                onSelect={(_, option) => selectStaff('bu_head', option.user)}
              />
            </Form.Item>

            <Form.Item
              name='bu_head_first_name'
              label={t('session.staff.approver_first_name', 'Approver {{level}} First Name', { level: 3 })}
              dependencies={['bu_head_email']}
              rules={[({ getFieldValue }) => ({
                validator: (_, value) => !getFieldValue('bu_head_email') || value ? Promise.resolve() : Promise.reject(new Error(t('session.staff.validation.approver_first_name', 'Approver {{level}} first name is required', { level: 3 })))
              })]}
            >
              <Input placeholder={t('common.first_name', 'First name')} />
            </Form.Item>

            <Form.Item
              name='bu_head_last_name'
              label={t('session.staff.approver_last_name', 'Approver {{level}} Last Name', { level: 3 })}
              dependencies={['bu_head_email']}
              rules={[({ getFieldValue }) => ({
                validator: (_, value) => !getFieldValue('bu_head_email') || value ? Promise.resolve() : Promise.reject(new Error(t('session.staff.validation.approver_last_name', 'Approver {{level}} last name is required', { level: 3 })))
              })]}
            >
              <Input placeholder={t('common.last_name', 'Last name')} />
            </Form.Item>

            {[4, 5].map(level => {
              const prefix = `approver_${level}`
              return (
                <div key={prefix}>
                  <Form.Item
                    name={`${prefix}_email`}
                    label={t('session.staff.approver_email_optional', 'Approver {{level}} Email (Optional)', { level })}
                    rules={[{ type: 'email', message: t('validation.invalid_email', 'Invalid email') }]}
                  >
                    <AutoComplete
                      placeholder={t('session.staff.search_approver', 'Search existing approver {{level}} or type email', { level })}
                      options={staffOptions(staffSearchResults[prefix])}
                      filterOption={false}
                      onSearch={value => searchStaff(prefix, value)}
                      onSelect={(_, option) => selectStaff(prefix, option.user)}
                    />
                  </Form.Item>
                  <Form.Item
                    name={`${prefix}_first_name`}
                    label={t('session.staff.approver_first_name', 'Approver {{level}} First Name', { level })}
                    dependencies={[`${prefix}_email`]}
                    rules={[({ getFieldValue }) => ({
                      validator: (_, value) => !getFieldValue(`${prefix}_email`) || value ? Promise.resolve() : Promise.reject(new Error(t('session.staff.validation.approver_first_name', 'Approver {{level}} first name is required', { level })))
                    })]}
                  >
                    <Input placeholder={t('common.first_name', 'First name')} />
                  </Form.Item>
                  <Form.Item
                    name={`${prefix}_last_name`}
                    label={t('session.staff.approver_last_name', 'Approver {{level}} Last Name', { level })}
                    dependencies={[`${prefix}_email`]}
                    rules={[({ getFieldValue }) => ({
                      validator: (_, value) => !getFieldValue(`${prefix}_email`) || value ? Promise.resolve() : Promise.reject(new Error(t('session.staff.validation.approver_last_name', 'Approver {{level}} last name is required', { level })))
                    })]}
                  >
                    <Input placeholder={t('common.last_name', 'Last name')} />
                  </Form.Item>
                </div>
              )
            })}

            <div style={{ display: 'flex', gap: 8 }}>
              <Button className='tti-btn-primary-grey' loading={staffSaving} onClick={saveStaff} style={{ flex: 2 }}>
                {editingAppraisal ? t('session.staff.actions.save', 'Save Staff Changes') : t('session.staff.actions.add_employee', 'Add Employee')}
              </Button>
              <Popconfirm
                title={t('session.staff.confirm.discard', 'Discard employee details?')}
                description={t('session.staff.confirm.discard_description', 'This will close the form and lose any unsaved changes.')}
                okText={t('common.discard', 'Discard')}
                cancelText={t('session.staff.actions.keep_editing', 'Keep Editing')}
                onConfirm={closeEmployeeModal}
              >
                <Button style={{ flex: 1 }}>{t('common.cancel', 'Cancel')}</Button>
              </Popconfirm>
            </div>
          </Form>
        </div>
      </Modal>

      <Modal
        title={t('session.import.title', 'Import Employees and Approvers (Excel)')}
        open={importModalOpen && !employeeListHidden}
        onCancel={() => setImportModalOpen(false)}
        footer={null}
      >
        <Upload
          accept='.xlsx'
          beforeUpload={handleImport}
          showUploadList={false}
        >
          <Button type='primary' size='large' icon={<UploadOutlined />} loading={importing}>{t('session.import.actions.select_file', 'Select Excel File (.xlsx)')}</Button>
        </Upload>
        <div>
          <Button
            type='link'
            icon={<DownloadOutlined />}
            loading={downloadingImportExample}
            onClick={handleDownloadImportExample}
            style={{ paddingLeft: 0 }}
          >
            {t('session.import.actions.download_example', 'Download Example Employee Import Excel Template')}
          </Button>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--tti-muted)' }}>
          {t('session.import.approver_help', 'Approver 1 is required. Approver 2 and Approver 3 are optional. Both current and legacy column headers are supported.')}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--tti-muted)' }}>
          {t('session.import.department_help', 'Department is optional reference information. Optional column: Joined Date. Use YYYY-MM-DD. Review periods start from the later of the session start date or employee joined date.')}
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>{t('session.import.upload', 'Upload')}</div>
            <Progress percent={importUploadProgress} />
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>{t('session.import.rows', 'Import rows')}</div>
            <Progress percent={importProgress} />
          </div>
          {importJobId && (
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
              {t('session.import.job', 'Job: {{id}}', { id: importJobId })}
            </div>
          )}
        </div>

        {importResult && (
          <div style={{ marginTop: 16 }}>
            <Text strong>{t('session.import.summary', 'Summary')}</Text>
            <pre style={{ background: 'var(--neutral-grey0)', padding: 12, borderRadius: 6, maxHeight: 240, overflow: 'auto' }}>{JSON.stringify(importResult, null, 2)}</pre>
            <Button onClick={() => setImportModalOpen(false)}>{t('common.close', 'Close')}</Button>
          </div>
        )}
      </Modal>
    </div>
  )
}